import { Controller } from "@hotwired/stimulus"

// Renders thumbnails for files selected in a `<input type="file" multiple>`
// and lets the user remove individual files before submitting.
//
// Used by the Nova Requisição "Anexos" section. Image files get an inline
// data-URL preview; non-images (rare here — input is accept="image/*"
// but the user could drag in a PDF) get a generic icon + filename.
export default class extends Controller {
  static targets = ["input", "list"]

  connect() {
    this.files = Array.from(this.inputTarget.files || [])
  }

  render(event) {
    const incoming = Array.from(event?.target?.files || this.inputTarget.files || [])
    this.files = this.mergeFiles(this.files, incoming)
    this.syncInputFiles()
    this.renderFiles()
  }

  renderFiles() {
    this.listTarget.innerHTML = ""
    if (this.files.length === 0) {
      this.listTarget.classList.add("hidden")
      return
    }
    this.listTarget.classList.remove("hidden")

    this.files.forEach((file, index) => {
      const card = this.buildCard(file, index)
      this.listTarget.appendChild(card)
    })
  }

  buildCard(file, index) {
    const card = document.createElement("div")
    card.className = "relative group rounded-lg border border-base-300 overflow-hidden bg-base-100"

    if (file.type.startsWith("image/")) {
      const img = document.createElement("img")
      img.alt = file.name
      img.className = "w-full h-28 object-cover bg-base-200"
      const reader = new FileReader()
      reader.onload = (e) => { img.src = e.target.result }
      reader.readAsDataURL(file)
      card.appendChild(img)
    } else {
      const placeholder = document.createElement("div")
      placeholder.className = "w-full h-28 flex items-center justify-center bg-base-200 text-base-content/50 text-xs"
      placeholder.textContent = file.type || "—"
      card.appendChild(placeholder)
    }

    const meta = document.createElement("div")
    meta.className = "p-2 text-[11px] text-base-content/70 truncate"
    meta.title = file.name
    meta.textContent = `${file.name} · ${this.humanSize(file.size)}`
    card.appendChild(meta)

    const removeBtn = document.createElement("button")
    removeBtn.type = "button"
    removeBtn.className = "absolute top-1 right-1 inline-flex items-center justify-center w-6 h-6 rounded-full bg-base-100/90 border border-base-300 text-base-content/70 hover:text-error hover:border-error transition"
    removeBtn.setAttribute("aria-label", "Remover")
    removeBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>'
    removeBtn.addEventListener("click", () => this.removeAt(index))
    card.appendChild(removeBtn)

    return card
  }

  // FileList is read-only, so we rebuild it via DataTransfer minus the
  // removed index, then re-render.
  removeAt(index) {
    this.files = this.files.filter((_file, i) => i !== index)
    this.syncInputFiles()
    this.renderFiles()
  }

  mergeFiles(existing, incoming) {
    const seen = new Set(existing.map((file) => this.fileKey(file)))
    const merged = [...existing]
    incoming.forEach((file) => {
      const key = this.fileKey(file)
      if (seen.has(key)) return
      seen.add(key)
      merged.push(file)
    })
    return merged
  }

  syncInputFiles() {
    const dt = new DataTransfer()
    this.files.forEach((file) => dt.items.add(file))
    this.inputTarget.files = dt.files
  }

  fileKey(file) {
    return [file.name, file.size, file.lastModified].join(":")
  }

  humanSize(bytes) {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`
  }
}
